/**
 * Error type hierarchy for application-wide error handling
 */

/**
 * Base application error class
 */
export class AppError extends Error {
  public code?: string;
  public originalError?: unknown;
  
  constructor(
    message: string,
    code?: string,
    originalError?: unknown
  ) {
    super(message);
    this.name = "AppError";
    this.code = code;
    this.originalError = originalError;
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }
}

/**
 * Frappe API error
 * Thrown when Frappe API returns an error response
 */
export class FrappeApiError extends AppError {
  public status?: number;
  public statusText?: string;
  
  constructor(
    message: string,
    status?: number,
    statusText?: string,
    originalError?: unknown
  ) {
    super(message, "FRAPPE_API_ERROR", originalError);
    this.name = "FrappeApiError";
    this.status = status;
    this.statusText = statusText;
  }
}

/**
 * Network error
 * Thrown when network request fails (timeout, connection error, etc.)
 */
export class NetworkError extends AppError {
  constructor(message: string, originalError?: unknown) {
    super(message, "NETWORK_ERROR", originalError);
    this.name = "NetworkError";
  }
}

/**
 * Validation error
 * Thrown when input validation fails
 */
export class ValidationError extends AppError {
  public field?: string;
  public errors?: string[];
  
  constructor(
    message: string,
    field?: string,
    errors?: string[],
    originalError?: unknown
  ) {
    super(message, "VALIDATION_ERROR", originalError);
    this.name = "ValidationError";
    this.field = field;
    this.errors = errors;
  }
}

/**
 * Authentication error
 * Thrown when authentication fails
 */
export class AuthenticationError extends AppError {
  constructor(message: string = "Authentication failed", originalError?: unknown) {
    super(message, "AUTHENTICATION_ERROR", originalError);
    this.name = "AuthenticationError";
  }
}

/**
 * Authorization error
 * Thrown when user lacks permission to perform an action
 */
export class AuthorizationError extends AppError {
  constructor(message: string = "Access denied", originalError?: unknown) {
    super(message, "AUTHORIZATION_ERROR", originalError);
    this.name = "AuthorizationError";
  }
}

/**
 * CSRF Token Error
 * Thrown when CSRF token is invalid or missing
 */
export class CSRFTokenError extends AuthenticationError {
  constructor(message: string = "CSRF token error. Please log in again.", originalError?: unknown) {
    super(message, originalError);
    this.name = "CSRFTokenError";
    this.code = "CSRF_TOKEN_ERROR";
  }
}

/**
 * Session Expired Error
 * Thrown when session has expired
 */
export class SessionExpiredError extends AuthenticationError {
  constructor(message: string = "Session expired. Please log in again.", originalError?: unknown) {
    super(message, originalError);
    this.name = "SessionExpiredError";
    this.code = "SESSION_EXPIRED";
  }
}

